import { routeAskId, routeGroupAskId } from "./types";
import type { ReadRouteStatus, RouteStatusKind } from "./types";

// Derived from the id helpers themselves, so a change to their shape cannot
// leave the parser reading the old one.
const ROUTE_GROUP_PREFIX = routeGroupAskId("");
const ROUTE_PREFIX = routeAskId("");

interface ParsedRouteAskId {
  circuitId: string;
  isGroup: boolean;
}

// Prefix-matched longest first, and the remainder is taken whole: splitting on
// ":" makes a circuit literally named "group" ambiguous, and circuit ids here
// carry "·" and can hold almost anything.
const parseRouteAskId = (askId: string): ParsedRouteAskId | null => {
  if (askId.startsWith(ROUTE_GROUP_PREFIX)) {
    return { circuitId: askId.slice(ROUTE_GROUP_PREFIX.length), isGroup: true };
  }
  if (askId.startsWith(ROUTE_PREFIX)) {
    return { circuitId: askId.slice(ROUTE_PREFIX.length), isGroup: false };
  }
  return null;
};

export const readRouteStatus: ReadRouteStatus = ({ settled, circuitIds }) => {
  const known = new Set(circuitIds);
  const status: Record<string, RouteStatusKind> = {};
  circuitIds.forEach((circuitId) => {
    status[circuitId] = "untouched";
  });

  // Answering the same ask again replaces the record in place, so the last one
  // settled is the only one that speaks for it.
  const lastOutcome = new Map<string, string>();
  settled.forEach(({ askId, outcome }) => {
    lastOutcome.set(askId, outcome);
  });

  const corrected = new Set<string>();

  lastOutcome.forEach((outcome, askId) => {
    const parsed = parseRouteAskId(askId);
    // A stale record from a previous run must never invent a row.
    if (!parsed || !known.has(parsed.circuitId)) return;

    if (parsed.isGroup) {
      corrected.add(parsed.circuitId);
      return;
    }

    // Only an explicit accept is a confirmation; every other way out of the
    // main ask ("committed", "declined") means he did something to the route.
    status[parsed.circuitId] =
      outcome === "accepted" ? "confirmed" : "corrected";
  });

  // The follow-up outranks the main ask in either record order: if he confirmed
  // the run and then moved devices off it, the truthful mark is corrected.
  corrected.forEach((circuitId) => {
    status[circuitId] = "corrected";
  });

  return status;
};
